import React from 'react';
import { SkeletonLine } from '@packages/ui/primitives/Skeleton';
import { HeaderRenderer } from './HeaderRenderer';
import type { InternalColumnDef } from '../schema/types';

interface TableLoadingSkeletonProps {
  columns: ReadonlyArray<InternalColumnDef>;
  showCheckbox: boolean;
  showActionsColumn: boolean;
}

/**
 * Renders only valid <table> children (colgroup/thead/tbody) for use inside DataTable.Content.
 * Matches legacy V1 pattern: real headers + skeleton body rows.
 */
export function TableLoadingSkeleton({
  columns,
  showCheckbox,
  showActionsColumn,
}: TableLoadingSkeletonProps): JSX.Element {
  return (
    <>
      <HeaderRenderer
        columns={columns}
        sortState={null}
        onSort={() => {}}
        showCheckbox={showCheckbox}
        onSelectAll={() => {}}
        isAllSelected={false}
        isPartiallySelected={false}
        showActionsColumn={showActionsColumn}
      />
      <tbody>
        {Array.from({ length: 6 }, (_, r) => (
          <tr key={r} data-testid={`dt-v2-skeleton-row-${r}`}>
            {showCheckbox && (
              <td style={{ textAlign: 'center' }}>
                <SkeletonLine size='body' width='18px' />
              </td>
            )}
            {columns.map((col, c) => {
              const colW = parseInt(String(col.width.default || '0'), 10);
              const skeletonW = colW > 0 ? `${Math.round(colW * 0.6)}px` : '75%';
              return (
                <td
                  key={c}
                  data-testid={`dt-v2-skeleton-cell-${r}-${c}`}
                  style={{ textAlign: col.align ?? 'left' }}>
                  <SkeletonLine size='body' width={skeletonW} />
                </td>
              );
            })}
            {showActionsColumn && (
              <td style={{ textAlign: 'center' }}>
                <SkeletonLine size='body' width='24px' />
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </>
  );
}
